import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useOrderRequestProductInputQuery } from '../../../../../../../../../services';
import { RhSingleAutocomplete } from '../../../../../../../../dum';
import { IOrderRequest } from '../../../order-request-form';
import { useCalculateProductsTotal } from '../../../helpers/use-calculate-products-total';
import {
    GROUP_PRICE,
    GROUP_WEIGHT,
    KILO_PRICE,
    ORDER_REQUEST_PRODUCTS,
    PRODUCT,
} from '../order-request-products-columns';
import { FIELD_LABELS, shortLabel } from '../../../../../../../../../helpers';

export function OrderProductionProductInput({ index }: { index: number }) {
    // The product picker is now scoped to the account's product catalog. The
    // catalog also fixes the prices, so selecting a product fills (and the
    // columns lock) kilo_price / group_price / group_weight from the catalog row.
    const accountId = useWatch({
        name: 'account_id',
    }) as IOrderRequest['account_id'];

    const { data } = useOrderRequestProductInputQuery({
        variables: {
            accountId: accountId || 0,
        },
        skip: !accountId,
    });

    const options = useMemo(() => {
        return (data?.getAccountProducts || [])
            .filter((accountProduct) => !!accountProduct.product)
            .map((accountProduct) => ({
                id: accountProduct.product!.id,
                compound_description:
                    accountProduct.product!.compound_description,
                kilo_price: accountProduct.kilo_price,
                group_price: accountProduct.group_price,
                group_weight: accountProduct.group_weight,
            }));
    }, [data]);

    const INPUT_NAME = `${ORDER_REQUEST_PRODUCTS}.${index}.${PRODUCT}`;
    const orderRequestProductRow = useWatch({
        name: `${ORDER_REQUEST_PRODUCTS}.${index}`,
    }) as IOrderRequest['order_request_products'][number];

    const formContext = useFormContext();

    const { calculateProductsTotal } = useCalculateProductsTotal();

    return (
        <RhSingleAutocomplete
            itemText={'compound_description'}
            itemValue={'id'}
            returnObject
            options={options}
            name={INPUT_NAME}
            label={shortLabel(FIELD_LABELS.product_id)}
            disableGutters
            disabled={!!orderRequestProductRow.id}
            onChange={(product) => {
                if (!!product && !orderRequestProductRow.id) {
                    const catalogProduct = options.find(
                        (p) => p.id === product.id,
                    );

                    if (catalogProduct) {
                        formContext.setValue(
                            `${ORDER_REQUEST_PRODUCTS}.${index}.${GROUP_PRICE}`,
                            catalogProduct.group_price,
                        );
                        formContext.setValue(
                            `${ORDER_REQUEST_PRODUCTS}.${index}.${KILO_PRICE}`,
                            catalogProduct.kilo_price,
                        );
                        formContext.setValue(
                            `${ORDER_REQUEST_PRODUCTS}.${index}.${GROUP_WEIGHT}`,
                            catalogProduct.group_weight || 0,
                        );
                    }
                    calculateProductsTotal();
                }
            }}
        />
    );
}
